import type { ProviderMigrationDetailDto } from './api/provider-migrations.api';

/**
 * Provider migration — THE WORKFLOW STATE MACHINE.
 *
 * ── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
 * A provider migration has SEVEN operator-visible phases, but the database has
 * only a `status` column, and that column does not distinguish the two phases
 * that matter most. `VERIFYING` means "the copy-and-verify phase finished" and
 * nothing more: the ownership service deliberately never writes the migration's
 * status, so a migration whose ownership has moved and one whose ownership has
 * not are both `VERIFYING`.
 *
 * The detail page used to key the Finalize panel on `status === 'VERIFYING'`
 * alone. That is precisely the state in which finalization gate
 * `ASSET_NOT_ON_TARGET_PROVIDER` is guaranteed to refuse, so the UI offered, as
 * the next action, the one action the server was certain to reject with a 409.
 * The missing ownership step then had to be run out-of-band by an engineer.
 *
 * Status alone is therefore not a workflow state. This module derives one, from
 * the status PLUS the server's ownership tally PLUS the item verification
 * counts, and every action the page offers is keyed on the result.
 *
 * ── PROVIDER-AGNOSTIC BY CONSTRUCTION ──────────────────────────────────────
 * No provider id, driver name, bucket or direction appears in this file. The
 * ownership prerequisite is expressed once, by the server, as "every asset of
 * this migration is owned by `migration.targetProviderId`" — which is the same
 * sentence for R2 -> S3, S3 -> R2, LOCAL -> R2 and R2 -> LOCAL. Grep this file
 * for `r2`, `s3` or `local`: there are none, and a test asserts it.
 *
 * ── FAIL CLOSED ────────────────────────────────────────────────────────────
 * Every uncertainty resolves AWAY from the destructive action:
 *
 *   • ownership tally absent (older backend) -> treated as NOT transferred, so
 *     the operator is routed to the read-only ownership rehearsal, never to
 *     Finalize;
 *   • ownership partially moved -> the ownership step, not Finalize;
 *   • items not all verified -> no mutating action at all;
 *   • an unrecognised status -> no mutating action at all.
 *
 * This module NEVER decides that something is safe; it only decides what to
 * offer. The server re-proves every claim independently and remains the sole
 * authority — nothing here can bypass a gate, because nothing here is consulted
 * by the gates.
 */

/** Statuses that mean "planned, nothing copied". */
const PLANNED_STATUSES = new Set(['PENDING', 'DRY_RUN', 'DRY_RUN_COMPLETE']);

export type MigrationStage =
  /** Scope calculated, nothing copied, awaiting approval. */
  | 'PLANNED'
  /** Approved (READY) — awaiting the copy-and-verify run. */
  | 'APPROVED'
  /** The copy is executing. */
  | 'RUNNING'
  /** A run stopped with failures. Needs diagnosis, not a retry button. */
  | 'PAUSED'
  /** Status VERIFYING but the items are not all verified. No action offered. */
  | 'VERIFY_INCOMPLETE'
  /** Verified; ownership still entirely on the source. The flip is next. */
  | 'OWNERSHIP_REQUIRED'
  /** Verified; ownership moved for SOME assets only. The flip is next. */
  | 'OWNERSHIP_PARTIAL'
  /** Verified AND every asset owned by the target. Finalization is next. */
  | 'FINALIZE_READY'
  | 'COMPLETED'
  | 'FAILED'
  | 'ROLLED_BACK'
  /**
   * Terminal, human-reviewed disposition: the destination copy was verified but
   * the ORIGINAL SOURCE objects are permanently gone, so source-side
   * verification can never complete. Offers no mutating action — the migration
   * can never be finalized, and ownership can no longer be flipped or rolled
   * back because both require status VERIFYING.
   */
  | 'SOURCE_IRRECOVERABLE'
  | 'UNKNOWN';

/** The phases shown in the stepper, in order. */
export const WORKFLOW_STEPS = [
  'Planned',
  'Approved',
  'Copying',
  'Verified',
  'Ownership',
  'Finalize',
  'Completed',
] as const;

export type WorkflowStep = (typeof WORKFLOW_STEPS)[number];

export interface MigrationWorkflow {
  stage: MigrationStage;
  /** Index into `WORKFLOW_STEPS` of the phase the migration is currently IN. */
  stepIndex: number;
  /** True once the server has proven every asset is owned by the target. */
  ownershipTransferred: boolean;
  /** True when the ownership tally is missing — treated as not transferred. */
  ownershipUnknown: boolean;

  // ── what the page may offer. Permission is already folded in. ─────────────
  canApproveAndRun: boolean;
  canRun: boolean;
  /** Read-only rehearsal. Needs only `platform:system:view`. */
  canPreviewOwnership: boolean;
  canFlipOwnership: boolean;
  /** Read-only rehearsal. Needs only `platform:system:view`. */
  canPreviewFinalize: boolean;
  canFinalize: boolean;

  /**
   * One sentence naming the operator's next step. Null when there is nothing
   * to do — a terminal migration, or one the console cannot advance.
   */
  nextActionLabel: string | null;
  /**
   * Why Finalize is NOT being offered, when the migration is otherwise at the
   * finalization stage. Null when Finalize is offered, or when the migration is
   * nowhere near it. This is the sentence the operator used to have to infer
   * from a raw 409.
   */
  finalizeBlockedReason: string | null;
}

function base(stage: MigrationStage, stepIndex: number): MigrationWorkflow {
  return {
    stage,
    stepIndex,
    ownershipTransferred: false,
    ownershipUnknown: false,
    canApproveAndRun: false,
    canRun: false,
    canPreviewOwnership: false,
    canFlipOwnership: false,
    canPreviewFinalize: false,
    canFinalize: false,
    nextActionLabel: null,
    finalizeBlockedReason: null,
  };
}

/**
 * Derive the workflow state.
 *
 * `canManage` is `platform:system:manage` — every MUTATING action is gated on
 * it. The two rehearsals are not: they write nothing and `platform:system:view`
 * is what the server requires for them, so a read-only operator can still see
 * exactly why a migration is stuck without being able to change anything.
 */
export function deriveMigrationWorkflow(
  migration: Pick<
    ProviderMigrationDetailDto,
    'status' | 'itemCount' | 'itemsVerified' | 'itemsByStatus' | 'ownership'
  >,
  canManage: boolean,
): MigrationWorkflow {
  const status = migration.status;

  if (status === 'COMPLETED') return base('COMPLETED', 6);
  if (status === 'FAILED') return base('FAILED', 2);
  if (status === 'ROLLED_BACK') return base('ROLLED_BACK', 4);

  if (PLANNED_STATUSES.has(status)) {
    const w = base('PLANNED', 0);
    // Only DRY_RUN_COMPLETE can be approved; the server refuses anything else.
    w.canApproveAndRun = canManage && status === 'DRY_RUN_COMPLETE';
    w.nextActionLabel = w.canApproveAndRun
      ? 'Approve the dry run and start the copy.'
      : 'This plan is not ready to approve yet.';
    return w;
  }

  if (status === 'READY') {
    const w = base('APPROVED', 1);
    w.canRun = canManage;
    w.nextActionLabel = 'Approved. Start the copy-and-verify run.';
    return w;
  }

  if (status === 'RUNNING') {
    const w = base('RUNNING', 2);
    w.nextActionLabel = 'The copy is running. Wait for it to finish.';
    return w;
  }

  if (status === 'PAUSED') {
    // Deliberately no action: a paused run means items failed verification,
    // which needs an operator to diagnose rather than a retry button.
    const w = base('PAUSED', 2);
    w.nextActionLabel = 'The run stopped with failures. Review the objects below.';
    return w;
  }

  if (status === 'SOURCE_IRRECOVERABLE') {
    // Terminal. Deliberately offers nothing: this is a recorded disposition,
    // not a workflow step. Finalization is impossible (the server refuses at
    // STATUS_NOT_VERIFYING), and ownership flip/rollback are equally
    // unreachable for the same reason. Every capability flag stays false.
    const w = base('SOURCE_IRRECOVERABLE', 5);
    w.nextActionLabel =
      'The destination copy was verified, but the original source objects are permanently gone. ' +
      'This migration can never be finalized and needs no further action.';
    w.finalizeBlockedReason =
      'Source-side verification can never succeed: the source objects no longer exist. ' +
      'Finalization requires them to be retained, so this migration was recorded as ' +
      'SOURCE_IRRECOVERABLE rather than COMPLETED.';
    return w;
  }

  if (status !== 'VERIFYING') return base('UNKNOWN', 0);

  // ── VERIFYING: the status that covers two different workflow phases ───────
  const allItemsCompleted =
    migration.itemCount > 0 && (migration.itemsByStatus['COMPLETED'] ?? 0) === migration.itemCount;
  const allVerified = migration.itemCount > 0 && migration.itemsVerified === migration.itemCount;

  if (!allItemsCompleted || !allVerified) {
    const w = base('VERIFY_INCOMPLETE', 3);
    w.nextActionLabel =
      'Not every object is verified yet. Neither ownership nor finalization can proceed.';
    w.finalizeBlockedReason =
      `Only ${migration.itemsVerified} of ${migration.itemCount} objects are verified. ` +
      'Finalization requires every object to be copied and checksum-verified.';
    return w;
  }

  const ownership = migration.ownership ?? null;

  // Ownership fully moved — and only the SERVER decides that. The counters are
  // never re-added up here; `transferred` is the single authority.
  if (ownership !== null && ownership.transferred) {
    const w = base('FINALIZE_READY', 5);
    w.ownershipTransferred = true;
    w.canPreviewFinalize = true;
    w.canFinalize = canManage;
    w.nextActionLabel = 'Ownership is on the target provider. This migration can be finalized.';
    return w;
  }

  // Everything else at VERIFYING is an ownership problem, including "we do not
  // know" — which resolves to the read-only rehearsal, never to Finalize.
  const w = base(
    ownership !== null && ownership.onTarget > 0 ? 'OWNERSHIP_PARTIAL' : 'OWNERSHIP_REQUIRED',
    4,
  );
  w.ownershipUnknown = ownership === null;
  w.canPreviewOwnership = true;
  w.canFlipOwnership = canManage;

  if (ownership === null) {
    w.nextActionLabel =
      'Objects are verified. Ownership must be confirmed on the target provider before finalization.';
    w.finalizeBlockedReason =
      'This server did not report where the objects are currently owned, so finalization is not ' +
      'offered. Run the read-only ownership rehearsal to find out.';
  } else if (w.stage === 'OWNERSHIP_PARTIAL') {
    w.nextActionLabel =
      `Objects are verified, but only ${ownership.onTarget} of ${ownership.totalAssets} are owned ` +
      'by the target provider. Ownership must be completed before finalization.';
    w.finalizeBlockedReason =
      `${ownership.totalAssets - ownership.onTarget} of ${ownership.totalAssets} objects are still ` +
      'not owned by the target provider. Finalization refuses at gate ' +
      'ASSET_NOT_ON_TARGET_PROVIDER until every one of them has moved.';
  } else {
    w.nextActionLabel =
      'Objects are verified. Ownership must now be moved to the target provider before finalization.';
    w.finalizeBlockedReason =
      `None of the ${ownership.totalAssets} objects are owned by the target provider yet. ` +
      'Finalization refuses at gate ASSET_NOT_ON_TARGET_PROVIDER until ownership has moved.';
  }
  return w;
}
